<template>
  <MarketplaceCard
    v-model="localConfig.enabled"
    :logo-path="ServiceFinanceLogo"
    logo-alt="ServiceFinance"
    description="Offer promotional and standard installment terms for your big tickets."
    learn-more-link="https://enroll.svcfin.com/dealer/enroll?id=47910"
    learn-more-text="Apply Now"
  >
    <template #configure>
      <div>
        <div
          data-cy="setupInformationLink"
          class="link"
          @click="toggleConfigModal"
        >
          Set Up Information
        </div>
      </div>
    </template>
    <template #modal>
      <Modal
        v-if="showConfigModal"
        title="Set Up Information - Service Finance"
        class-name="service-finance-config-modal"
        :saving="saving"
        @submit="saveIntegration"
        @hide-modal="toggleConfigModal"
        @delete="confirmDelete"
      >
        <div class="d-flex flex-column">
          <TextField
            v-model="localConfig.dealer_id"
            class="full-width"
            name="Dealer ID"
            data-cy="dealerId"
            placeholder=""
            :error-message="firstErrorMessage(v$.dealer_id)"
          />
        </div>
      </Modal>
      <Modal
        v-if="showDeleteModal"
        title="Delete Service Finance Configuration"
        @hide-modal="toggleDeleteModal"
      >
        <div>
          This configuration will be permanently deleted and you will not be
          able to undo this action. Are you sure?
        </div>
        <template #submit>
          <Button
            id="submit_modal_btn"
            data-cy="dropIntegration"
            type="danger"
            @click="dropIntegration"
          >
            Delete Configuration
          </Button>
        </template>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import TextField from "@/components/TextField.vue";
import ServiceFinanceLogo from "@/images/financing/service_finance.png";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import MarketplaceCard from "./MarketplaceCard.vue";

const store = useStore();

const localConfig = ref({
  enabled: false,
  dealer_id: ""
});

const saving = ref(false);
const showConfigModal = ref(false);
const showDeleteModal = ref(false);

const serviceFinance = computed(() => store.state.serviceFinance);

const v$ = useVuelidate(
  {
    dealer_id: { required }
  },
  localConfig
);

watch(showConfigModal, (newValue) => {
  if (newValue) initForm();
});

watch(
  () => localConfig.value.enabled,
  (newValue, oldValue) => {
    if (
      oldValue === null ||
      newValue === oldValue ||
      serviceFinance.value?.config.enabled === newValue
    )
      return;
    if (newValue) {
      enableIntegration();
    } else {
      disableIntegration();
    }
  }
);

watch(serviceFinance, (newValue, oldValue) => {
  if (newValue !== oldValue && oldValue !== undefined) {
    initForm();
  }
});

onMounted(async () => {
  await store.dispatch("fetchServiceFinanceConfig");
});

async function enableIntegration() {
  try {
    await store.dispatch("changeServiceFinanceConfig", { enabled: true });
    window.notify.success("Service Finance integration enabled!");
  } catch (e) {
    localConfig.value.enabled = false;
  }
}

async function disableIntegration() {
  await store.dispatch("changeServiceFinanceConfig", { enabled: false });
  window.notify.success("Service Finance integration disabled!");
}

async function dropIntegration() {
  showDeleteModal.value = false;
  showConfigModal.value = false;
  localConfig.value.enabled = false;
  if (serviceFinance.value.config.persisted) {
    await store.dispatch("dropServiceFinanceIntegration");
    window.notify.success("Service Finance settings deleted!");
  } else {
    window.notify.error(
      "The integration has not been configured yet. There's nothing to delete."
    );
  }
}

function confirmDelete() {
  toggleDeleteModal();
}

function initForm() {
  v$.value.$reset();
  localConfig.value = {
    enabled: serviceFinance.value?.config?.enabled || false,
    dealer_id: serviceFinance.value?.config?.dealer_id || ""
  };
}

async function saveIntegration() {
  const valid = await v$.value.$validate();
  if (!valid) return;
  saving.value = true;
  try {
    await store.dispatch("changeServiceFinanceConfig", {
      ...serviceFinance.value.config,
      ...localConfig.value
    });
    window.notify.success("Service Finance settings saved!");
    toggleConfigModal();
  } catch (e) {
    // do nothing
  }
  saving.value = false;
}

function toggleConfigModal() {
  showConfigModal.value = !showConfigModal.value;
}

function toggleDeleteModal() {
  showDeleteModal.value = !showDeleteModal.value;
}
</script>
